import DialogContentText from "@material-ui/core/DialogContentText";
import ActionDialog from "@saleor/components/ActionDialog";
import NotFoundPage from "@saleor/components/NotFoundPage";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import VariantImagePage from "../components/VariantImagePage";
import {
  useVariantImageDeleteMutation,
  useVariantImageUpdateMutation
} from "../mutations";
import { useVariantImageQuery } from "../queries";
import {
  productListUrl,
  productVariantEditUrl,
  variantImageUrl,
  VariantImageUrlQueryParams, 
} from "../urls";

interface VariantImageProps {
  productId: string;
  imageId: string;
  variantId: string;
  params: VariantImageUrlQueryParams;
}

export const VariantImage: React.FC<VariantImageProps> = ({
  productId,
  imageId,
  variantId,
  params
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const handleBack = () => navigate(productVariantEditUrl(productId,variantId));

  const { data, loading } = useVariantImageQuery({
    displayLoader: true,
    variables: {
      imageId,
      variantId
    }
  });

  const [updateImage, updateResult] = useVariantImageUpdateMutation({
    onCompleted: data => {
      if (data.variantImageUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const [deleteImage, deleteResult] = useVariantImageDeleteMutation({
    onCompleted: handleBack
  });

  const variant = data?.productVariant;

  if (variant === null) {
    return <NotFoundPage onBack={() => navigate(productListUrl())} />;
  }

  const handleDelete = () => deleteImage({ variables: { id: imageId } });
  const handleImageClick = (id: string) => () =>
    navigate(variantImageUrl(productId,variantId, id));
  const handleUpdate = (formData: { description: string }) => {
    updateImage({
      variables: {
        alt: formData.description,
        id: imageId
      }
    });
  };
  const image = data?.productVariant?.mainImage;

  return (
    <>
      <VariantImagePage
        disabled={loading}
        variant={data?.productVariant?.skuName||data?.productVariant?.name}
        image={image || null}
        images={data?.productVariant?.images}
        onBack={handleBack}
        onDelete={() =>
          navigate(
            variantImageUrl(productId,variantId, imageId, {
              action: "remove"
            })
          )
        }
        onRowClick={handleImageClick}
        onSubmit={handleUpdate}
        saveButtonBarState={updateResult.status}
      />
      <ActionDialog
        onClose={() => navigate(variantImageUrl(productId,variantId, imageId), true)}
        onConfirm={handleDelete}
        open={params.action === "remove"}
        title={intl.formatMessage({
          defaultMessage: "Delete Image",
          description: "dialog header"
        })}
        variant="delete"
        confirmButtonState={deleteResult.status}
      >
        <DialogContentText>
          <FormattedMessage defaultMessage="Are you sure you want to delete this image?" />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};
export default VariantImage;
